"use client";

import { createContext, useContext, useMemo } from "react";

import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import {
  categoryLabel,
  joinNames,
  sectionCategories,
  unavailableCategories,
} from "@/features/talent-pools/lib/filter-categories";
import { useListTalentPools } from "@/lib/api/generated";
import type { TalentPoolResponse } from "@/lib/api/model";

/** Category id → selected pools that turn it off. Empty outside a search session. */
type CategoryGate = ReadonlyMap<string, TalentPoolResponse[]>;

const NO_GATE: CategoryGate = new Map();
const PoolCategoryGateContext = createContext<CategoryGate>(NO_GATE);

/**
 * Provides the selected pools' disabled categories to every `FilterSection`
 * below it. Only a search session has pools; elsewhere nothing is gated.
 */
export function PoolCategoryGateProvider({ children }: { children: React.ReactNode }) {
  const { poolIds } = useTalentSearchFilters();
  const { data } = useListTalentPools({ query: { enabled: !!poolIds?.length } });
  const gate = useMemo<CategoryGate>(
    () => (poolIds?.length && data ? unavailableCategories(data.items, poolIds) : NO_GATE),
    [data, poolIds],
  );
  return (
    <PoolCategoryGateContext.Provider value={gate}>{children}</PoolCategoryGateContext.Provider>
  );
}

/**
 * Why a filter section is unavailable for the selected pools, or null when
 * every pool supports it.
 */
export function useSectionUnavailableReason(sectionKey: string): string | null {
  const gate = useContext(PoolCategoryGateContext);
  const categories = sectionCategories(sectionKey).filter((c) => gate.has(c));
  if (categories.length === 0) return null;
  const pools = [...new Set(categories.flatMap((c) => gate.get(c)!.map((p) => p.name)))];
  return `Not available in ${joinNames(pools)}: ${pools.length === 1 ? "this pool doesn't" : "these pools don't"} support ${joinNames(categories.map(categoryLabel))} filters.`;
}
